import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';

const CONTACT_FALLBACK = {
  address: 'Block C, Ansal Sushant City, Sector 61, Kundli, Sonipat, Haryana 131028',
  phone: '9266333940, 9266333941',
  email: 'career@sawssonipat.in',
  timings: 'Monday to Saturday: 8:30 AM - 4:00 PM',
};

const PERKS_FALLBACK = [
  {
    icon: 'fa-chalkboard-user',
    title: 'Inspiring Academic Culture',
    desc: 'Collaborate with forward-thinking educators and experienced academic mentors in a student-centered ecosystem.',
  },
  {
    icon: 'fa-laptop-code',
    title: 'Smart Learning Classrooms',
    desc: 'Access interactive smart classrooms, composite science laboratories, coding hubs, and comprehensive library assets.',
  },
  {
    icon: 'fa-arrow-trend-up',
    title: 'Continuous Professional Growth',
    desc: 'Periodic CBSE-aligned pedagogical workshops, educational seminars, and career progression pathways.',
  },
  {
    icon: 'fa-hand-holding-heart',
    title: 'Supportive Welfare & Culture',
    desc: 'Competitive compensation scales, campus transport assistance, and an inclusive, collegial work environment.',
  },
];

const JobApplicationForm = () => {
  const [pageData, setPageData] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    qualification: '',
    experience: '',
    address: '',
    contact_no: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let active = true;
    API.get('/pages/public/job-application-form')
      .then((res) => {
        if (active && res.data?.data) {
          setPageData(res.data.data);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    try {
      await API.post('/enquiries/public/job-application', form);
      setSubmitted(true);
      setForm({ name: '', email: '', qualification: '', experience: '', address: '', contact_no: '' });
    } catch (err) {
      setErrorMsg('Error submitting your application. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const sections = pageData?.sections || {};
  const banner = sections.banner || {};
  const intro = sections.intro || {};
  const contactInfo = { ...CONTACT_FALLBACK, ...(sections.contact_info || {}) };
  const perks = sections.perks && sections.perks.length > 0 ? sections.perks : PERKS_FALLBACK;

  const bannerImgSrc = pageData?.banner_image
    ? (pageData.banner_image.startsWith('http') || pageData.banner_image.startsWith('/')
        ? pageData.banner_image
        : `${UPLOADS_URL}/${pageData.banner_image}`)
    : '/images/page-banners/job-application.jpg';

  const title = banner.title || pageData?.title || 'Careers & Opportunities';
  const subtitle = banner.subtitle || 'Join our passionate team of educators and mentors shaping young minds.';
  const breadcrumb = banner.breadcrumb || 'CAREERS';
  const kicker = intro.kicker || 'Join Our Faculty & Staff';
  const heading = intro.heading || 'Build Your Career at St. Andrews';
  const description =
    intro.description ||
    'We welcome passionate educators and dedicated professionals who are committed to nurturing intellectual curiosity and holistic student development. Explore opportunities and submit your profile below.';

  const phoneNumbers = (contactInfo.phone || '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <>
      <SEO
        slug="job-application-form"
        title={`${title} | St. Andrews World School, Sonipat`}
        description="Explore faculty and career opportunities at St. Andrews World School, Sonipat. Submit your CV and application online."
        keywords="Careers, Teaching Jobs, School Jobs Sonipat, Faculty Recruitment, St Andrews World School"
      />

      {/* ─── HERO BANNER ─────────────────────────────────────────── */}
      <PageBanner
        title={title}
        subtitle={subtitle}
        breadcrumb={breadcrumb}
        bgImage={bannerImgSrc}
        fallback="/images/page-banners/job-application.jpg"
      />

      {/* ─── INTRO SECTION ────────────────────────────────────────── */}
      <section className="py-12 md:py-16 bg-white border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="container">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#a4862f] bg-amber-50 px-3 py-1 rounded-full border border-amber-200/60 inline-block mb-3">
                {kicker}
              </span>
              <h2
                className="text-3xl sm:text-4xl md:text-5xl font-serif text-[#1b3160] leading-tight"
                style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}
              >
                {heading}
              </h2>
              {description && (
                <p className="mt-4 text-base md:text-lg text-gray-600 leading-relaxed font-light">
                  {description}
                </p>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── MAIN CONTENT: PERKS + APPLICATION FORM ────────────────── */}
      <section className="py-14 md:py-20 bg-[#fbfbfa]">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            
            {/* LEFT COLUMN: Why Join Us & Contact Card (5 cols) */}
            <div className="lg:col-span-5 space-y-8">
              {/* Institutional Highlights */}
              <Reveal>
                <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xs border border-gray-100 space-y-6">
                  <div className="border-b border-gray-100 pb-4">
                    <span className="text-xs font-bold uppercase tracking-widest text-[#a4862f]">Why St. Andrews</span>
                    <h3
                      className="text-2xl font-serif text-[#1b3160] mt-1"
                      style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}
                    >
                      Empowering Educators
                    </h3>
                  </div>

                  <div className="space-y-5">
                    {perks.map((perk, idx) => (
                      <div key={idx} className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-amber-50 text-[#a4862f] flex items-center justify-center shrink-0 border border-amber-200/50 shadow-xs">
                          <i className={`fas ${perk.icon || 'fa-star'} text-sm`}></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 leading-snug">{perk.title}</h4>
                          <p className="text-xs text-gray-600 mt-1 leading-relaxed">{perk.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>

              {/* Campus Recruitment Contacts Card */}
              <Reveal>
                <div className="bg-gradient-to-br from-[#1b3160] to-[#0c1a30] text-white rounded-2xl p-6 md:p-8 shadow-md space-y-5">
                  <div className="border-b border-white/10 pb-3">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-[#f0c24b]">HR & Careers Desk</span>
                    <h4 className="text-lg text-white font-serif font-semibold mt-1">Get in Touch Directly</h4>
                  </div>

                  <div className="space-y-4 text-xs text-gray-200">
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center shrink-0 text-[#f0c24b]">
                        <i className="fas fa-map-marker-alt"></i>
                      </div>
                      <span className="leading-relaxed">{contactInfo.address}</span>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center shrink-0 text-[#f0c24b]">
                        <i className="fas fa-phone"></i>
                      </div>
                      <div className="space-y-0.5">
                        {phoneNumbers.map((phone, i) => (
                          <div key={i}>
                            <a
                              href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
                              className="hover:text-[#f0c24b] transition-colors"
                            >
                              {phone}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center shrink-0 text-[#f0c24b]">
                        <i className="fas fa-envelope"></i>
                      </div>
                      <a
                        href={`mailto:${contactInfo.email}`}
                        className="hover:text-[#f0c24b] transition-colors break-all"
                      >
                        {contactInfo.email}
                      </a>
                    </div>

                    {contactInfo.timings && (
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center shrink-0 text-[#f0c24b]">
                          <i className="fas fa-clock"></i>
                        </div>
                        <span className="leading-relaxed">{contactInfo.timings}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            </div>

            {/* RIGHT COLUMN: Application Form (7 cols) */}
            <div className="lg:col-span-7">
              <Reveal>
                <div className="bg-white rounded-2xl p-7 md:p-10 shadow-sm border border-gray-100">
                  <div className="border-b border-gray-100 pb-5 mb-7">
                    <span className="text-xs font-bold uppercase tracking-widest text-[#a4862f]">Apply Online</span>
                    <h3
                      className="text-2xl sm:text-3xl font-serif text-[#1b3160] mt-1"
                      style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}
                    >
                      Candidate Application Form
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1">
                      Please enter accurate details. Fields marked with <span className="text-red-500">*</span> are mandatory.
                    </p>
                  </div>

                  {submitted ? (
                    <div className="text-center py-12 px-6 bg-emerald-50/60 border border-emerald-200/80 rounded-2xl space-y-4">
                      <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto text-2xl shadow-xs">
                        <i className="fas fa-check"></i>
                      </div>
                      <h4
                        className="text-2xl font-serif text-gray-900"
                        style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}
                      >
                        Application Submitted Successfully!
                      </h4>
                      <p className="text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
                        Thank you for your interest in joining St. Andrews World School, Sonipat. Our recruitment board will review your credentials and contact shortlisted applicants for further rounds.
                      </p>
                      <div className="pt-2">
                        <button
                          type="button"
                          onClick={() => setSubmitted(false)}
                          className="px-6 py-2.5 rounded-xl bg-[#1b3160] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#a4862f] transition-colors shadow-sm inline-flex items-center gap-2 cursor-pointer"
                        >
                          <i className="fas fa-plus text-xs"></i> Submit Another Application
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {errorMsg && (
                        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-center gap-2">
                          <i className="fas fa-circle-exclamation text-base shrink-0"></i>
                          <span>{errorMsg}</span>
                        </div>
                      )}

                      {/* Name */}
                      <div className="space-y-1.5">
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#1b3160]">
                          Full Name of the Candidate <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          required
                          placeholder="e.g. Dr. Rajesh Sharma"
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50/40 focus:bg-white focus:border-[#a4862f] focus:outline-none transition-all shadow-xs"
                        />
                      </div>

                      {/* Email & Phone */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold uppercase tracking-wider text-[#1b3160]">
                            Email Address <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            required
                            placeholder="e.g. rajesh@example.com"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50/40 focus:bg-white focus:border-[#a4862f] focus:outline-none transition-all shadow-xs"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold uppercase tracking-wider text-[#1b3160]">
                            Contact Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            value={form.contact_no}
                            onChange={(e) => setForm({ ...form, contact_no: e.target.value })}
                            required
                            placeholder="e.g. 9876543210"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50/40 focus:bg-white focus:border-[#a4862f] focus:outline-none transition-all shadow-xs"
                          />
                        </div>
                      </div>

                      {/* Qualification & Experience */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold uppercase tracking-wider text-[#1b3160]">
                            Highest Qualification <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={form.qualification}
                            onChange={(e) => setForm({ ...form, qualification: e.target.value })}
                            required
                            placeholder="e.g. M.Sc (Physics), B.Ed, CTET"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50/40 focus:bg-white focus:border-[#a4862f] focus:outline-none transition-all shadow-xs"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label className="block text-xs font-bold uppercase tracking-wider text-[#1b3160]">
                            Relevant Experience <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={form.experience}
                            onChange={(e) => setForm({ ...form, experience: e.target.value })}
                            required
                            placeholder="e.g. 5 Years PGT Physics"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50/40 focus:bg-white focus:border-[#a4862f] focus:outline-none transition-all shadow-xs"
                          />
                        </div>
                      </div>

                      {/* Residential Address */}
                      <div className="space-y-1.5">
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#1b3160]">
                          Residential Address <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          rows="3"
                          value={form.address}
                          onChange={(e) => setForm({ ...form, address: e.target.value })}
                          required
                          placeholder="Complete address with city, state, and pin code"
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50/40 focus:bg-white focus:border-[#a4862f] focus:outline-none transition-all shadow-xs resize-y"
                        ></textarea>
                      </div>

                      {/* Submit Button */}
                      <div className="pt-2">
                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-[#1b3160] hover:bg-[#a4862f] text-white text-xs font-bold uppercase tracking-widest transition-all duration-200 shadow-sm flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {loading ? (
                            <>
                              <i className="fas fa-circle-notch fa-spin text-sm"></i>
                              <span>Submitting Application...</span>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-paper-plane text-xs"></i>
                              <span>Submit Application</span>
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </Reveal>
            </div>

          </div>
        </div>
      </section>
    </>
  );
};

export default JobApplicationForm;
